import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { FeatureFlagContext } from "../FeatureFlagProvider/FeatureFlagProvider"
import { defaultSettings } from "../FeatureFlagProvider/default"
import { StartupSettings } from "../FeatureFlagProvider/types"
import { RetryHooks, withRetry } from "./withRetry"

export type UseRetryOptions<T> = {
  /** Per-call overrides on top of the context's startupSettings. */
  settings?: Partial<StartupSettings>
  shouldRetry?: RetryHooks["shouldRetry"]
  onRetry?: RetryHooks["onRetry"]
  onSuccess?: (data: T) => void
  onError?: (error: unknown) => void
}

export type UseRetryResult<T, Args extends unknown[]> = {
  data: T | undefined
  error: unknown
  isLoading: boolean
  /** 0-based attempt index of the in-flight call. */
  attempt: number
  execute: (...args: Args) => Promise<T>
  cancel: () => void
  reset: () => void
}

/**
 * React binding for withRetry. Pulls retry settings from FeatureFlagContext
 * (startupSettings), so components stay in sync with startup config. Falls back
 * to defaultSettings when read outside a provider, and per-call `settings`
 * override on top of that.
 */
export const useRetry = <T, Args extends unknown[] = []>(
  fn: (...args: Args) => Promise<T>,
  options: UseRetryOptions<T> = {}
): UseRetryResult<T, Args> => {
  const { startupSettings } = useContext(FeatureFlagContext)

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<unknown>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [attempt, setAttempt] = useState(0)

  // Latest fn/options/settings via ref so execute() stays stable and never
  // runs against a stale closure.
  const fnRef = useRef(fn)
  const optsRef = useRef(options)
  const settingsRef = useRef(startupSettings)
  useEffect(() => {
    fnRef.current = fn
    optsRef.current = options
    settingsRef.current = startupSettings
  })

  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    if( mountedRef.current ) {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    cancel()
    setData(undefined)
    setError(undefined)
    setIsLoading(false)
    setAttempt(0)
  }, [cancel])

  const execute = useCallback(async (...args: Args): Promise<T> => {
    abortRef.current?.abort() // supersede any in-flight call
    const controller = new AbortController()
    abortRef.current = controller
    const { signal } = controller

    const opts = optsRef.current
    // Context settings first, then per-call overrides. defaultSettings is the
    // final fallback inside withRetry, so this is safe even when startupSettings
    // is the empty context default.
    const settings: Partial<StartupSettings> = {
      ...defaultSettings,
      ...settingsRef.current,
      ...opts.settings,
    }

    const isCurrent = () => abortRef.current === controller && !signal.aborted
    const safeSet = <S,>(setter: (v: S) => void, value: S) => {
      if (mountedRef.current && isCurrent()) setter(value)
    }

    safeSet(setIsLoading, true)
    safeSet(setError, undefined)
    safeSet(setAttempt, 0)

    try {
      const result = await withRetry<T>(
        (n) => {
          safeSet(setAttempt, n)
          return fnRef.current(...args)
        },
        settings,
        {
          signal,
          shouldRetry: opts.shouldRetry,
          onRetry: opts.onRetry,
        }
      )
      if (isCurrent()) {
        safeSet(setData, result)
        safeSet(setIsLoading, false)
        opts.onSuccess?.(result)
      }
      return result
    } catch (err) {
      if (isCurrent()) {
        safeSet(setError, err)
        safeSet(setIsLoading, false)
        opts.onError?.(err)
      }
      throw err
    }
  }, [])

  return { data, error, isLoading, attempt, execute, cancel, reset }
}
